import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Center,
} from '@chakra-ui/react';

import { Footer } from './Footer';
import { Content } from './Content';
import { Filter, FilterGroup, IFilters } from 'api-paths-cas';
import { FilterSetup } from './FilterSetup';
import { Spinner } from '../../atoms/spinner';

export interface IProps {
  isOpen: boolean;
  filters: IFilters;
  filterGroups: FilterGroup[];
  loading: boolean;
  gotoFilter?: Filter;
  closeFiltersPanel: () => void;
  clearAllFilters: () => void;
  onSubmit?: () => void;
  onSubmitLabel?: string;
  onFilterConfirmLabel?: string;
  hasClearAllFilters?: boolean;
  hasClearThisFilters?: boolean;
  hasCancelButton?: boolean;
  isValid?: boolean;
  initialSearchValue?: string;
}
export interface IDispatchProps {
  saveFilter: (key: string, values: string[] | number[]) => void;
  filterGroupExpanded: (groupName: string, expanded: boolean) => void;
  saveSearchValue?: (searchString: string) => void;
}

export const FiltersPanel = (props: IProps & IDispatchProps): React.ReactElement => {
  const {
    isOpen,
    gotoFilter,
    filters,
    filterGroups,
    closeFiltersPanel,
    clearAllFilters,
    saveFilter,
    filterGroupExpanded,
    onSubmit,
    onSubmitLabel,
    hasClearAllFilters,
    onFilterConfirmLabel,
    hasClearThisFilters,
    hasCancelButton,
    isValid,
    loading,
    saveSearchValue,
    initialSearchValue,
  } = props;

  const [filterToSetup, setFilterToSetup] = useState<undefined | Filter>();
  const [filterToSetupKey, setFilterToSetupKey] = useState<undefined | string>();

  const closeFilterSetup = (): void => {
    setFilterToSetup(undefined);
    setFilterToSetupKey(undefined);
    if (saveSearchValue) saveSearchValue('');
  };

  const openFilterSetup = (filterKey: string): void => {
    setFilterToSetupKey(filterKey);
  };

  useEffect(() => {
    if (gotoFilter) {
      setFilterToSetupKey(gotoFilter.key);
      return;
    }
    closeFilterSetup();
  }, [isOpen, gotoFilter]);

  const closeHandler = (): void => {
    closeFiltersPanel();
    closeFilterSetup();

    if (onSubmit) onSubmit(); // TODO: wrap this in logic that only applies if enabledfilters > 1
  };

  useEffect(() => {
    if (!filterToSetupKey) {
      return;
    }
    let currentFilterOpen: undefined | Filter;

    for (const filterGroup of filterGroups) {
      currentFilterOpen = filterGroup.filters.find(filter => filter.key === filterToSetupKey);
      if (currentFilterOpen) {
        break;
      }
    }
    setFilterToSetup(currentFilterOpen);
  }, [filterGroups, filterToSetupKey]);
  const filterAccordionStyles = filterToSetup
    ? { overflow: 'hidden', width: 0, height: 0, position: 'absolute' as const, top: '-1000px', zIndex: '-1' }
    : {};
  return (
    <Drawer onClose={closeHandler} isOpen={isOpen || !!filterToSetup} size="xs" placement="left">
      <DrawerOverlay />
      <DrawerContent data-selector="filters-panel">
        <DrawerCloseButton />

        {loading && (
          <Center
            position={'absolute'}
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={10}
            bgColor={'whiteAlpha.400'}
            _dark={{ bgColor: 'blackAlpha.500' }}
          >
            <Spinner size="32px" />
          </Center>
        )}

        {filterToSetup && (
          <FilterSetup
            loading={loading}
            filter={filterToSetup}
            activeFilters={filters.activeFilters}
            onCloseFilterSetup={closeFilterSetup}
            saveFilter={saveFilter}
            hasClearThisFilters={hasClearThisFilters}
            onFilterConfirmLabel={onFilterConfirmLabel}
            opened={Boolean(filterToSetup)}
            onSearchChange={saveSearchValue}
            initialSearchValue={initialSearchValue}
          />
        )}

        <DrawerHeader {...filterAccordionStyles}>Filters</DrawerHeader>

        <DrawerBody {...filterAccordionStyles}>
          <Content
            filters={filters}
            filterGroups={filterGroups}
            openFilterSetup={openFilterSetup}
            filterGroupExpanded={filterGroupExpanded}
          />
        </DrawerBody>

        {!filterToSetup ? (
          <DrawerFooter>
            <Footer
              closeFiltersPanel={closeHandler}
              clearAllFilters={clearAllFilters}
              onSubmit={onSubmit}
              onSubmitLabel={onSubmitLabel}
              hasClearAllFilters={hasClearAllFilters}
              hasCancelButton={hasCancelButton}
              isValid={isValid}
            />
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
};
